import React, { useEffect } from 'react';

import {
  useInvitationRequestFormState,
  useInvitationRequestFormActions
} from './InvitationRequestFormContext';
import Form from '../../Shared/UI/Form/components/Form';
import FormInstruction from '../../Shared/UI/FormInstruction/components/FormInstruction';
import FormRow from '../../Shared/UI/FormRow/components/FormRow';
import Field from '../../Shared/UI/Field/components/Field';
import Input from '../../Shared/UI/Input/components/Input';
import Checkbox from '../../Shared/UI/Checkbox/components/Checkbox';
import CheckboxGroup from '../../Shared/UI/CheckboxGroup/components/CheckboxGroup';
import ChoiceRow from '../../Shared/UI/ChoiceRow/components/ChoiceRow';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ResponsiveContainer from '../../Shared/UI/ResponsiveContainer/components/ResponsiveContainer';

import { createUserInvitationRequest } from './restApi';

const USE_CASE_OPTIONS = [
  { key: 'job_search', label: 'Job Search' },
  { key: 'talent_search', label: 'Talent Search' },
  { key: 'networking', label: 'Networking' },
  { key: 'other', label: 'Other' },
];

const InvitationRequestForm = ({ closeInvitationRequestWizard }) => {
  const state = useInvitationRequestFormState();
  const {
    initializeState,
    updateFirstName,
    updateLastName,
    updateEmail,
    updateLinkedInProfileUrl,
    updatePersonalWebsiteOrBlogUrl,
    toggleExpectedUseCase,
    updateExpectedUseCasesOther,
    onResponse,
    onError,
  } = useInvitationRequestFormActions();

  useEffect(() => {
    initializeState();
  }, []);

  const buildExpectedUseCases = () => {
    const selectedUseCases = Object.entries(state?.invitationRequestForm?.expectedUseCases || {})
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => key);

    const result = { useCases: selectedUseCases };

    if (state?.invitationRequestForm?.expectedUseCases?.other && state?.invitationRequestForm?.expectedUseCasesOther) {
      result.other = state.invitationRequestForm.expectedUseCasesOther;
    }

    return result;
  };

  const handleSubmit = () => {
    createUserInvitationRequest({
      onResponse,
      onError,
      body: {
        firstName: state?.invitationRequestForm?.firstName || '',
        lastName: state?.invitationRequestForm?.lastName || '',
        email: state?.invitationRequestForm?.email || '',
        linkedInProfileUrl: state?.invitationRequestForm?.linkedInProfileUrl || '',
        personalWebsiteOrBlogUrl: state?.invitationRequestForm?.personalWebsiteOrBlogUrl || '',
        expectedUseCases: buildExpectedUseCases(),
        authenticityToken: document.querySelector("meta[name='csrf-token']").getAttribute("content"),
      },
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormInstruction>
        Thanks for your interest in using Isibute.
      </FormInstruction>
      <FormInstruction>
        Please request an invitation by submitting your information below.
      </FormInstruction>

      <FormRow>
        <Field label="First Name" id="firstName">
          <Input
            type="text"
            value={state?.invitationRequestForm?.firstName || ''}
            onChange={(e) => updateFirstName(e.target.value)}
          />
        </Field>

        <Field label="Last Name" id="lastName">
          <Input
            type="text"
            value={state?.invitationRequestForm?.lastName || ''}
            onChange={(e) => updateLastName(e.target.value)}
          />
        </Field>
      </FormRow>

      <Field label="Email" id="email">
        <Input
          type="email"
          value={state?.invitationRequestForm?.email || ''}
          onChange={(e) => updateEmail(e.target.value)}
        />
      </Field>

      <Field label="LinkedIn Profile" hint="Optional" id="linkedinProfileUrl">
        <Input
          type="url"
          value={state?.invitationRequestForm?.linkedInProfileUrl || ''}
          onChange={(e) => updateLinkedInProfileUrl(e.target.value)}
        />
      </Field>

      <Field label="Personal Website/Blog" hint="Optional" id="personalWebsiteOrBlog">
        <Input
          type="url"
          value={state?.invitationRequestForm?.personalWebsiteOrBlogUrl || ''}
          onChange={(e) => updatePersonalWebsiteOrBlogUrl(e.target.value)}
        />
      </Field>

      <Field label="How do you expect to use Isibute most?" id="expectedUseCases">
        <CheckboxGroup>
          {USE_CASE_OPTIONS.map((option) => (
            <ChoiceRow key={option.key}>
              <Checkbox
                checked={state?.invitationRequestForm?.expectedUseCases?.[option.key] || false}
                onChange={() => toggleExpectedUseCase(option.key)}
              >
                {option.label}
              </Checkbox>
            </ChoiceRow>
          ))}
        </CheckboxGroup>
      </Field>

      {state?.invitationRequestForm?.expectedUseCases?.other && (
        <Field label="Please tell us more" id="expectedUseCasesOther">
          <Input
            type="text"
            value={state?.invitationRequestForm?.expectedUseCasesOther || ''}
            onChange={(e) => updateExpectedUseCasesOther(e.target.value)}
          />
        </Field>
      )}

      <ResponsiveContainer name="form-actions">
        <FormActions>
          <Button
            variant="secondary"
            onClick={() => {
              closeInvitationRequestWizard();
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            Get an invitation
          </Button>
        </FormActions>
      </ResponsiveContainer>
    </Form>
  );
};

export default InvitationRequestForm;
